import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  useTheme,
  styled
} from '@mui/material';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import RefreshIcon from '@mui/icons-material/Refresh';
import SecurityIcon from '@mui/icons-material/Security';

// Styled components
const FeatureCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: 20,
  transition: 'all 0.3s ease',
  overflow: 'visible',
  position: 'relative',
  '&:hover': {
    transform: 'translateY(-10px)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
    '& .icon-wrapper': {
      transform: 'rotateY(180deg)',
    }
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: 80,
  height: 80,
  borderRadius: 16,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(3),
  transition: 'transform 0.5s ease',
  background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
  boxShadow: `0 10px 20px rgba(94, 53, 177, 0.2)`,
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  position: 'relative',
  display: 'inline-block',
  marginBottom: theme.spacing(1),
  '&:after': {
    content: '""',
    position: 'absolute',
    bottom: -10,
    left: 0,
    width: 80,
    height: 4,
    borderRadius: 2,
    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  }
}));

const FeaturesSection: React.FC = () => {
  const theme = useTheme();

  const features = [
    {
      icon: <ShowChartIcon sx={{ fontSize: 40, color: 'white' }} />,
      title: 'Аналитика в реальном времени',
      description: 'Отслеживайте продажи и активность ваших NFT с обновлениями через OpenSea API. Получайте актуальные данные в любой момент времени.'
    },
    {
      icon: <EqualizerIcon sx={{ fontSize: 40, color: 'white' }} />,
      title: 'Расширенная статистика',
      description: 'Анализируйте ключевые метрики: средняя цена, выручка, прибыль, ROI. Выявляйте тренды и оптимизируйте свою коллекцию.'
    },
    {
      icon: <RefreshIcon sx={{ fontSize: 40, color: 'white' }} />,
      title: 'Обновление по запросу',
      description: 'Обновляйте данные вручную с помощью кнопки «Обновить». Полный контроль над актуальностью информации.'
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 40, color: 'white' }} />,
      title: 'Безопасная авторизация',
      description: 'Защищенная авторизация через Firebase Authentication. Ваши данные в безопасности.'
    }
  ];

  return (
    <Box 
      sx={{ 
        py: { xs: 10, md: 15 },
        background: 'white',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Decorative elements */}
      <Box
        sx={{
          position: 'absolute',
          width: '800px',
          height: '800px',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${theme.palette.primary.light}10 0%, transparent 70%)`,
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 0,
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography 
            variant="overline" 
            component="div"
            sx={{ 
              color: theme.palette.primary.main, 
              fontWeight: 600,
              letterSpacing: 2,
              mb: 2 
            }}
          >
            ВОЗМОЖНОСТИ ПЛАТФОРМЫ
          </Typography>
          <SectionTitle variant="h2" fontWeight="700">
            Расширенные инструменты аналитики
          </SectionTitle>
          <Typography 
            variant="h5" 
            color="text.secondary"
            sx={{ 
              maxWidth: 700, 
              mx: 'auto', 
              mt: 3,
              fontWeight: 400,
              lineHeight: 1.6
            }}
          >
            Наша платформа предоставляет всю необходимую информацию для успешного управления вашими NFT-коллекциями
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <FeatureCard elevation={4}>
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <IconWrapper className="icon-wrapper">
                    {feature.icon}
                  </IconWrapper>
                  <Typography variant="h5" fontWeight="600" gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </FeatureCard>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default FeaturesSection; 